// =============================================================================
// jobDescriptionFormat — JOB-WALK-2 fix #1: an honest formatter for "About
// this role" (JobDetail.tsx). Jason's own live-walk repro (Anthropic ANZ,
// 2026-07-05): the served description rendered as one flat, pre-wrap blob —
// headings ran straight into their following paragraph, bullets ran together
// as one sentence. The root cause was upstream (JobExtractionService.ts's
// stripHtml used to flatten every HTML tag to a single space, destroying the
// source's own paragraph/list-item boundaries before the text ever reached
// the DB) — THAT is fixed at the data path. This module is the FE half: now
// that a freshly-extracted description carries real newline structure (blank
// lines between paragraphs, "- " bullet markers), this turns it into real
// <p>/<ul><li> blocks instead of one <div style="white-space:pre-wrap">.
//
// NO INVENTION, applied to structure: this module NEVER guesses a heading or
// a list where the text doesn't already signal one. It only recognizes
// structure the text ITSELF carries:
//   - a blank line (\n\n, or an OS-style \r\n\r\n) = a paragraph boundary —
//     the one structural signal virtually every text source can carry,
//     including a plain user paste that never touched stripHtml at all.
//   - a line starting with -, •, *, or a `N.`/`N)` numbered marker = a bullet
//     item; a RUN of consecutive bullet lines becomes one <ul>.
//   - a short (<=70 char) standalone line ending in ':' = a conservative
//     section-header signal (e.g. "Responsibilities:", "What you'll need:")
//     — deliberately narrow (length-capped, colon-anchored, whole-line) so a
//     normal sentence that happens to end a paragraph with a colon is never
//     misclassified; when in doubt this heuristic does NOT fire and the line
//     is just a normal (short) paragraph.
// Every other line — including a description with ZERO structural signal at
// all (a thin paste-import predating this fix, or a source whose own markup
// never survived extraction) — degrades to a single paragraph block holding
// the ENTIRE text verbatim. That is the explicit "when in doubt, paragraph
// it" contract this module is built to keep: less structure, never invented
// structure.
//
// Pure, framework-free (no React/DOM) — unit-testable standalone, mirrors
// jobBuckets.ts / locationOrder.ts's own pure-formatter shape. JobDetail.tsx
// maps the returned blocks to real <p>/<ul><li> JSX; this module never
// renders anything itself.
//
// FIX 2 (FE-FIXES lane, 2026-07-05) — an HTML SAFETY NET, belt-and-braces.
// The real fix for raw HTML leaking into a served description is a DATA-path
// fix (the NORMALIZER lane's own scope: apps/api's text-ingest/
// textNormalize.ts) — this module never re-implements that upstream
// responsibility. But this is the LAST renderer before a description reaches
// the screen, and "the FE must never render raw tags again" is a real,
// separate law from "the data should already be clean": if a served
// description still carries an HTML tag (a row written before the
// normalizer fix landed, or any other upstream gap this module can't see),
// stripHtmlTagsToText degrades it to clean text BEFORE block-formatting,
// so the worst case is one honest paragraph — never a literal "<div>" on
// screen. Already-clean text (the overwhelmingly common case, and every
// existing test fixture above) passes through this step completely
// unchanged (no tag pattern matches, so the guard is a no-op). This is
// explicitly a DEFENSIVE NET, not a data fix — never a substitute for the
// normalizer's own upstream cleanup.
// =============================================================================

export type JobDescriptionBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'heading'; text: string }
  | { kind: 'bullets'; items: string[] };

/** Detects a raw HTML tag anywhere in the text — the exact shape a stray
 *  un-normalized row would carry (`<div>`, `</p>`, `<br/>`, `<li class="x">`,
 *  etc.). Case-insensitive; the leading `[a-zA-Z]` anchors to a real tag name
 *  so a bare "<" used as a less-than sign in ordinary prose ("scores < 5")
 *  never false-positives (no letter immediately follows that `<`). */
const HTML_TAG_PATTERN = /<\/?[a-zA-Z][^>]*>/;

/** Strip every HTML tag from `text`, replacing each with a single space (so
 *  "<p>One</p><p>Two</p>" degrades to "One Two", never "OneTwo") and
 *  collapsing the run of blank-space this can leave behind back down to at
 *  most a normal word-gap — WITHOUT touching real newlines (a `\n\n`
 *  paragraph boundary the text already carries must survive this step
 *  intact, so a later stripped-clean description can still be recognized as
 *  multiple paragraphs, not flattened to one). Pure text transform — no DOM
 *  parser, no dangerouslySetInnerHTML, never executes anything found inside
 *  a tag; this only ever produces a plain string. */
function stripHtmlTagsToText(text: string): string {
  return text
    .replace(/<\/?[a-zA-Z][^>]*>/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();
}

/** A bullet-marker prefix: -, –, •, *, or a numbered "1." / "2)" form. Anchored
 *  to the START of the (already-trimmed) line, with at least one space after
 *  the marker, so a hyphenated WORD ("Well-being requirements") is never
 *  misread as a bullet — a real marker is always its own token. */
const BULLET_PREFIX = /^(?:[-–•*]|\d{1,2}[.)])\s+/;

/** A conservative section-header signal: the ENTIRE (trimmed) line is short
 *  and ends in a colon — "Responsibilities:", "What you'll need:". Capped at
 *  70 chars so a genuine sentence that merely ends with a colon (a much
 *  longer clause introducing a list) is never misclassified; a real section
 *  label in practice is always a short phrase. */
const HEADING_LIKE = /^.{1,70}:$/;

/** Strip a line's leading bullet marker, returning just the item text. */
function stripBulletPrefix(line: string): string {
  return line.replace(BULLET_PREFIX, '').trim();
}

/** Split raw description text into paragraph-level blocks on a blank line
 *  (one or more fully-empty lines between non-empty lines) — the one
 *  structural signal every source can carry, independent of whether the
 *  upstream extraction fix ran. Normalizes \r\n to \n first so a Windows-
 *  origin paste splits identically to a Unix-origin one. */
function splitOnBlankLines(text: string): string[] {
  return text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);
}

/** Parse one paragraph-level block (already split on blank lines) into ONE OR
 *  MORE JobDescriptionBlocks. A block that is a single short colon-terminated
 *  line becomes a heading; a block whose lines are ALL bullet-prefixed
 *  becomes one bullets block; a block with a MIX of bullet and non-bullet
 *  lines runs each line through the same classification independently (a
 *  bullet run within it still groups into one <ul>, consecutive non-bullet
 *  lines still join back into one paragraph) — this is what makes a
 *  Greenhouse-style "heading, then a run of bullets, all inside one
 *  blank-line-delimited block" render correctly without needing every
 *  bullet's OWN blank-line separation (extraction emits one blank line
 *  between </li>-closed items, which already produces separate blocks in
 *  practice, but this stays correct even when a source's blank-line
 *  placement is looser). */
function parseBlock(block: string): JobDescriptionBlock[] {
  const lines = block.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return [];

  // A single standalone short colon-terminated line -> a heading. Only ever
  // fires for a ONE-line block (the conservative "be conservative" mandate:
  // a multi-line block is prose/a list, never a heading).
  if (lines.length === 1 && !BULLET_PREFIX.test(lines[0]) && HEADING_LIKE.test(lines[0])) {
    return [{ kind: 'heading', text: lines[0].slice(0, -1).trim() }];
  }

  const out: JobDescriptionBlock[] = [];
  let bulletRun: string[] = [];
  let paragraphRun: string[] = [];

  const flushBullets = () => {
    if (bulletRun.length > 0) {
      out.push({ kind: 'bullets', items: bulletRun });
      bulletRun = [];
    }
  };
  const flushParagraph = () => {
    if (paragraphRun.length > 0) {
      out.push({ kind: 'paragraph', text: paragraphRun.join(' ') });
      paragraphRun = [];
    }
  };

  for (const line of lines) {
    if (BULLET_PREFIX.test(line)) {
      flushParagraph();
      bulletRun.push(stripBulletPrefix(line));
    } else {
      flushBullets();
      paragraphRun.push(line);
    }
  }
  flushBullets();
  flushParagraph();
  return out;
}

/**
 * Format a job description into an honest, ordered list of display blocks.
 * Pure — no DOM, no React. Returns [] for a null/empty/whitespace-only
 * description (the caller already has its own "no description" empty state;
 * this never fabricates a placeholder block).
 *
 * Every block's text is a VERBATIM substring of the input (paragraphs are
 * lines rejoined with a single space; bullets/headings have only their own
 * marker/colon stripped) — this function reorders nothing and invents no
 * words, it only recognizes boundaries the text already contains.
 */
export function formatJobDescription(description: string | null | undefined): JobDescriptionBlock[] {
  if (!description || !description.trim()) return [];
  // FIX 2 — the defensive net: only ever engaged when a raw HTML tag is
  // actually present (the common, already-clean case takes this branch zero
  // times and is byte-for-byte unaffected). See this module's own header for
  // why this is a belt-and-braces LAST LINE, never a substitute for the
  // data-path fix.
  const safeDescription = HTML_TAG_PATTERN.test(description)
    ? stripHtmlTagsToText(description)
    : description;
  if (!safeDescription.trim()) return [];
  const blocks = splitOnBlankLines(safeDescription);
  // A description with no blank-line structure at all (the pre-fix legacy
  // shape, or a thin single-sentence paste) is exactly ONE block here — it
  // still runs through parseBlock so a lone heading-shaped line or an
  // in-paragraph bullet run (e.g. a paste that kept single newlines between
  // bullets but never a true blank line) is still recognized, while genuinely
  // unstructured prose degrades to a single paragraph, per this module's own
  // "when in doubt, paragraph it" law.
  return blocks.flatMap(parseBlock);
}
